import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Empty, Spin, Table, Tag } from 'antd-v5';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useHistory } from 'react-router-dom';
import { Section } from '../../components/Section';
import { useEvents } from '../../../../../hooks/query/useEvents';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const HeaderCell = props => (
  <th
    {...props}
    className="header-cell-no-separator"
    style={{ ...props.style, backgroundColor: 'transparent', color: '#575757' }}
  />
);

const isEventInProgress = event => {
  const now = dayjs();
  if (!event.startDate || !event.endDate) return false;
  return dayjs(event.startDate).isSameOrBefore(now, 'day') && dayjs(event.endDate).isSameOrAfter(now, 'day');
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate) return 'No dates set';
  const start = dayjs(startDate);
  const end = endDate ? dayjs(endDate) : null;

  if (!end || start.isSame(end, 'day')) {
    return start.format('ddd MMM D, YYYY');
  }
  if (start.isSame(end, 'month')) {
    return `${start.format('ddd MMM D')} - ${end.format('ddd MMM D, YYYY')}`;
  }
  return `${start.format('ddd MMM D, YYYY')} - ${end.format('ddd MMM D, YYYY')}`;
};

const TodayEvents = ({ auth }) => {
  const history = useHistory();

  // Associates: filter by their store. Managers: their assigned stores. Admins: show all events.
  const storeIds = useMemo(() => {
    if (auth.role === 'ASSOCIATE' && auth.storeId) {
      return String(auth.storeId);
    }
    if (auth.role === 'MANAGER') {
      return (auth.stores || [])
        .filter(s => s.id !== 0)
        .map(s => s.id)
        .join(',');
    }
    return '';
  }, [auth.role, auth.storeId, auth.stores]);

  const { data: eventsData, isLoading } = useEvents({
    query: {
      offset: 0,
      limit: 100,
      ...(storeIds ? { storeIds } : {}),
    },
    useQueryOptions: {
      staleTime: 30 * 1000,
      enabled: !!auth.merchantId,
    },
  });

  const events = useMemo(() => {
    const rawEvents = eventsData?.data || eventsData || [];
    const now = dayjs();

    // Filter to only upcoming and in-progress events (endDate >= today), then sort by date and show first 5
    return rawEvents
      .filter(event => {
        if (!event.endDate) return true;
        return dayjs(event.endDate).isSameOrAfter(now, 'day');
      })
      .sort((a, b) => {
        // Sort by startDate (latest first)
        const aDate = a.startDate ? dayjs(a.startDate) : null;
        const bDate = b.startDate ? dayjs(b.startDate) : null;

        if (!aDate && !bDate) return 0;
        if (!aDate) return 1; // Events without dates go to the end
        if (!bDate) return -1;

        return bDate.valueOf() - aDate.valueOf();
      })
      .slice(0, 5);
  }, [eventsData]);

  // Hide section when no events
  if (!isLoading && events.length === 0) {
    return null;
  }

  const handleRowClick = record => {
    history.push(`/Events/event-details/${record.id}`);
  };

  const columns = [
    {
      title: 'Event',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{text || 'Untitled Event'}</span>
          {isEventInProgress(record) && <Tag color="success">In Progress</Tag>}
        </div>
      ),
    },
    {
      title: 'Dates',
      key: 'dates',
      render: (_, record) => formatDateRange(record.startDate, record.endDate),
    },
    {
      title: 'Stores',
      key: 'stores',
      render: (_, record) => {
        const sessionStores = record.sessions?.flatMap(s => s.stores || []) || [];
        let uniqueStores = [...new Map(sessionStores.map(s => [s.id, s])).values()];
        if (uniqueStores.length === 0) {
          const sessionStoreIds = record.sessions?.flatMap(s => s.storeIds || []) || [];
          const uniqueIds = [...new Set(sessionStoreIds)];
          if (uniqueIds.length > 0) {
            const allStores = auth?.stores?.filter(s => s.id !== 0) || [];
            const storeMap = new Map(allStores.map(s => [s.id, s]));
            uniqueStores = uniqueIds.map(id => storeMap.get(id)).filter(Boolean);
          }
        }
        if (uniqueStores.length === 0) return '-';
        if (uniqueStores.length <= 2) return uniqueStores.map(s => s.name).join(', ');
        return `${uniqueStores[0].name} +${uniqueStores.length - 1} more`;
      },
    },
  ];

  return (
    <Section title="Events" viewAllUrl="/all-events" className="m-btm-35" data-testid="events__today--section">
      {isLoading ? (
        <div className="w-100-P flex-col-center m-top-10">
          <Spin tip="Loading..." size="large" indicator={<LoadingOutlined spin />}>
            <div
              style={{
                padding: 50,
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 8,
              }}
            />
          </Spin>
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={events}
          rowKey="id"
          pagination={false}
          size="middle"
          bordered={false}
          className="today-table"
          components={{ header: { cell: HeaderCell } }}
          onRow={record => ({
            'data-testid': 'events__today--row',
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No events today" />,
          }}
        />
      )}
    </Section>
  );
};

const mapStateToProps = state => ({ auth: state.auth });
export default connect(mapStateToProps)(TodayEvents);
